import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
    this.#initializeCustomFields(modalContent);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }

  /**
   * Initialize custom field components in the modal
   * @param {Element} modalContent - The modal content element
   */
  #initializeCustomFields(modalContent) {
    // Clear initialization flags from managers to allow re-attachment
    this.#clearInitializationFlags(modalContent);
    
    // Initialize custom select dropdowns
    this.#initCustomSelects(modalContent);
    
    // Initialize custom multiselect components
    this.#initCustomMultiselects(modalContent);
    
    // Force re-initialization of file upload components
    this.#reinitFileUploads(modalContent);
    
    // Initialize conditional display logic
    this.#initConditionalFields(modalContent);

      // Re-attach fee handlers to product forms in the modal
      // @ts-ignore - customOptionFeesManager is added by custom-option-fees.js
      if (window.customOptionFeesManager) {
        // @ts-ignore
        window.customOptionFeesManager.attachToProductForms();
      }

      // Re-attach price with fees listeners
      // @ts-ignore - productPriceWithFeesManager is added by product-price-with-fees.js
      if (window.productPriceWithFeesManager) {
        // DON'T call attachListeners() here - it would attach to modal inputs
        // but use document.querySelector() which would update the product card price
        // instead of the modal price. We handle modal price updates manually below.
        
        // Helper function to update modal price
        const updateModalPrice = async (/** @type {HTMLFormElement} */ form) => {
          // @ts-ignore
          const fees = await window.productPriceWithFeesManager.collectSelectedFees(form);
          // @ts-ignore
          const total = fees.reduce((sum, f) => sum + f.price, 0);
          
          const formComponent = form.closest('product-form-component');
          // @ts-ignore
          const productId = formComponent instanceof HTMLElement ? formComponent.dataset.productId : null;
          
          // Update the price in the modal (not the product card)
          const priceElementInModal = modalContent.querySelector(`product-price[data-product-id="${productId}"]`);
          
          if (priceElementInModal) {
            const priceContainer = priceElementInModal.querySelector('[ref="priceContainer"]');
            const priceSpan = priceContainer?.querySelector('.price');
            
            if (priceSpan instanceof HTMLElement) {
              // Store original price if not already stored
              if (!priceSpan.dataset.originalPrice) {
                priceSpan.dataset.originalPrice = priceSpan.textContent?.trim() || '';
              }
              
              // @ts-ignore
              const originalPriceCents = window.productPriceWithFeesManager.parsePriceToCents(priceSpan.dataset.originalPrice);
              const newTotalCents = originalPriceCents + total;
              // @ts-ignore
              const newPriceFormatted = window.productPriceWithFeesManager.formatPrice(newTotalCents);
              
              priceSpan.textContent = newPriceFormatted;
              
              // Add fee indicator
              let feeIndicator = priceContainer?.querySelector('.price-fee-indicator');
              if (!feeIndicator && total > 0 && priceContainer) {
                feeIndicator = document.createElement('span');
                feeIndicator.className = 'price-fee-indicator';
                if (feeIndicator instanceof HTMLElement) {
                  feeIndicator.style.cssText = 'display: block; font-size: 0.85em; color: rgb(var(--color-foreground-rgb) / 0.7); margin-top: 0.25rem;';
                }
                priceContainer.appendChild(feeIndicator);
              }
              
              if (feeIndicator) {
                if (total > 0) {
                  // @ts-ignore
                  const feeFormatted = window.productPriceWithFeesManager.formatPrice(total);
                  feeIndicator.textContent = `(Includes ${feeFormatted} in fees)`;
                } else {
                  feeIndicator.remove();
                }
              }
            }
          }
        };
        
        // Add change listener to fee inputs to update modal price
        const feeInputs = modalContent.querySelectorAll('[data-fee-config] input, [data-fee-config] select, [data-fee-config] textarea');
        feeInputs.forEach((input) => {
          if (input instanceof HTMLElement) {
            input.addEventListener('change', async () => {
              const form = input.closest('form');
              if (form) {
                await updateModalPrice(form);
              }
            });
          }
        });
        
        // Add quantity change listeners to update modal price
        const productForms = modalContent.querySelectorAll('product-form-component');
        productForms.forEach((formComponent) => {
          const form = formComponent instanceof HTMLElement ? formComponent.querySelector('form') : null;
          if (form) {
            // Listen for quantity-selector:update events at the form level
            form.addEventListener('quantity-selector:update', async () => {
              await updateModalPrice(form);
            });
            
            // Also listen on the quantity input directly as a fallback
            const quantityInput = form.querySelector('input[name="quantity"]');
            if (quantityInput instanceof HTMLInputElement) {
              quantityInput.addEventListener('change', async () => {
                await updateModalPrice(form);
              });
              
              // Debounce input events to avoid excessive recalculation
              /** @type {number | undefined} */
              let debounceTimer;
              quantityInput.addEventListener('input', async () => {
                if (debounceTimer) {
                  clearTimeout(debounceTimer);
                }
                debounceTimer = setTimeout(async () => {
                  await updateModalPrice(form);
                }, 300);
              });
            }
          }
        });
      }
  }

  /**
   * Clear initialization flags to allow re-initialization
   * @param {Element} modalContent - The modal content element
   */
  #clearInitializationFlags(modalContent) {
    // Clear fee manager flags from product forms
    const productForms = modalContent.querySelectorAll('product-form-component');
    productForms.forEach((form) => {
      if (form instanceof HTMLElement) {
        delete form.dataset.feesAttached;
        delete form.dataset.feePriceInitialized;
      }
    });

    // Clear fee price listener flags on inputs
    const feeInputs = modalContent.querySelectorAll('[data-fee-config] input, [data-fee-config] select, [data-fee-config] textarea');
    feeInputs.forEach((input) => {
      if (input instanceof HTMLElement) {
        delete input.dataset.feePriceListenerAttached;
        delete input.dataset.feePriceQuantityListenerAttached;
      }
    });

    // Clear conditional field initialization flags
    const conditionalFields = modalContent.querySelectorAll('[data-cond-key]');
    conditionalFields.forEach((field) => {
      if (field instanceof HTMLElement) {
        delete field.dataset.condInit;
      }
    });
  }

  /**
   * Initialize custom select dropdowns in the modal
   * @param {Element} container - The container element
   */
  #initCustomSelects(container) {
    const customSelects = container.querySelectorAll('.js-custom-select');
    
    customSelects.forEach((enhanced) => {
      if (!enhanced || !(enhanced instanceof HTMLElement)) return;
      if (enhanced.dataset.initialized) return;
      
      enhanced.dataset.initialized = 'true';
      
      const button = enhanced.querySelector('.custom-select__button');
      const valueEl = enhanced.querySelector('.custom-select__value');
      const menu = enhanced.querySelector('.custom-select__menu');
      const select = enhanced.querySelector('select');
      const allOptions = Array.from(menu?.querySelectorAll('.custom-select__option:not(.custom-select__option--placeholder)') || []);

      if (!button || !valueEl || !menu || !select) return;

      // Initialize label from current select value
      const selected = select.selectedOptions?.[0];
      if (selected) {
        valueEl.textContent = selected.textContent || '';
      }

      const openMenu = () => {
        if (menu instanceof HTMLElement) {
          menu.hidden = false;
        }
        button.setAttribute('aria-expanded', 'true');
        if (allOptions[0] instanceof HTMLElement) {
          allOptions[0].focus();
        }
      };

      const closeMenu = () => {
        if (menu instanceof HTMLElement) {
          menu.hidden = true;
        }
        button.setAttribute('aria-expanded', 'false');
      };

      const toggleMenu = () => {
        const isHidden = menu instanceof HTMLElement && menu.hidden;
        isHidden ? openMenu() : closeMenu();
      };

      const selectValue = (/** @type {string} */ value, /** @type {string} */ displayText) => {
        const match = Array.from(select.options).find(o => o.value === value);
        if (match) {
          select.value = match.value;
        } else {
          select.selectedIndex = 0;
        }
        valueEl.textContent = displayText || value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        closeMenu();
      };

      button.addEventListener('click', (e) => {
        e.preventDefault();
        toggleMenu();
      });

      document.addEventListener('click', (e) => {
        if (!enhanced.contains(/** @type {Node} */ (e.target))) {
          closeMenu();
        }
      });

      menu.addEventListener('click', (e) => {
        const li = (/** @type {Element} */ (e.target)).closest('.custom-select__option');
        if (!li || li.classList.contains('custom-select__option--placeholder')) return;
        if (li instanceof HTMLElement && li.dataset.value) {
          selectValue(li.dataset.value, li.textContent || '');
        }
      });

      menu.addEventListener('keydown', (e) => {
        if (!(e instanceof KeyboardEvent)) return;
        if (!(e.target instanceof HTMLElement)) return;
        const idx = allOptions.indexOf(e.target);
        
        if (e.key === 'Escape') {
          closeMenu();
          if (button instanceof HTMLElement) {
            button.focus();
          }
        } else if (e.key === 'ArrowDown') {
          e.preventDefault();
          const next = allOptions[Math.min(idx + 1, allOptions.length - 1)];
          if (next instanceof HTMLElement) next.focus();
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          const prev = allOptions[Math.max(idx - 1, 0)];
          if (prev instanceof HTMLElement) prev.focus();
        } else if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (e.target instanceof HTMLElement && e.target.dataset.value) {
            selectValue(e.target.dataset.value, e.target.textContent || '');
          }
        }
      });
    });
  }

  /**
   * Initialize custom multiselect components in the modal
   * @param {Element} container - The container element
   */
  #initCustomMultiselects(container) {
    const multiselects = container.querySelectorAll('.custom-multi');
    
    multiselects.forEach((root) => {
      if (!(root instanceof HTMLElement)) return;
      if (root.dataset.multiInit) return;
      
      root.dataset.multiInit = 'true';
      
      const hidden = root.querySelector('input[type="hidden"][name^="properties["]');
      const checkboxes = Array.from(root.querySelectorAll('.checkbox__input'));
      const separator = root.dataset.separator || ', ';
      
      const sync = () => {
        const values = checkboxes
          .filter(c => c instanceof HTMLInputElement && c.checked)
          .map(c => (/** @type {HTMLInputElement} */ (c)).value);
        
        if (hidden instanceof HTMLInputElement) {
          const before = hidden.value;
          hidden.value = values.join(separator);
          if (hidden.value !== before) {
            hidden.dispatchEvent(new Event('input', { bubbles: true }));
            hidden.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      };
      
      checkboxes.forEach(cb => {
        if (cb instanceof HTMLInputElement) {
          cb.addEventListener('change', sync);
        }
      });
      
      sync();
    });
  }

  /**
   * Force re-initialization of file upload components
   * @param {Element} container - The container element
   */
  #reinitFileUploads(container) {
    const fileUploads = container.querySelectorAll('product-file-upload');
    
    fileUploads.forEach((upload) => {
      if (!(upload instanceof HTMLElement)) return;
      
      const fileInput = upload.querySelector('input[type="file"]');
      if (!fileInput) return;
      
      // The morphing breaks the component's internal event listeners
      // We need to manually set them up again
      const buttons = upload.querySelectorAll('button[on\\:click="/triggerUpload"]');
      const dropzone = upload.querySelector('.file-upload-dropzone');
      
      // Remove old listeners by cloning input
      const newInput = fileInput.cloneNode(true);
      if (fileInput.parentNode) {
        fileInput.parentNode.replaceChild(newInput, fileInput);
      }
      
      // Set up button click handlers
      buttons.forEach((btn) => {
        const newBtn = btn.cloneNode(true);
        if (btn.parentNode) {
          btn.parentNode.replaceChild(newBtn, btn);
        }
        
        newBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const input = upload.querySelector('input[type="file"]');
          if (input instanceof HTMLInputElement) {
            input.click();
          }
        });
      });
      
      // Set up dropzone if it exists
      if (dropzone) {
        const newDropzone = dropzone.cloneNode(true);
        if (dropzone.parentNode) {
          dropzone.parentNode.replaceChild(newDropzone, dropzone);
        }
        
        newDropzone.addEventListener('click', (e) => {
          e.preventDefault();
          const input = upload.querySelector('input[type="file"]');
          if (input instanceof HTMLInputElement) {
            input.click();
          }
        });
      }
    });
  }

  /**
   * Initialize conditional display fields
   * @param {Element} container - The container element
   */
  #initConditionalFields(container) {
    const conditionalFields = container.querySelectorAll('[data-cond-key]');
    
    conditionalFields.forEach((field) => {
      if (!(field instanceof HTMLElement)) return;
      if (field.dataset.condInit) return;
      
      const key = field.getAttribute('data-cond-key');
      if (!key) return;
      
      const formId = field.getAttribute('data-form');
      const form = (formId && document.getElementById(formId)) || field.closest('form');
      
      const evaluateCondition = () => {
        const op = field.getAttribute('data-cond-op') || 'has_any_value';
        const values = (field.getAttribute('data-cond-values') || '')
          .split(',')
          .map(s => s.trim())
          .filter(Boolean)
          .map(s => s.toLowerCase());
        
        const value = this.#readFormValue(form, key);
        let show = false;
        
        if (value.found) {
          const v = (value.value || '').trim().toLowerCase();
          if (op === 'has_any_value') {
            show = v !== '';
          } else if (op === 'is_any_of') {
            show = values.length ? values.includes(v) : v !== '';
          } else if (op === 'is_not_any_of') {
            show = values.length ? !values.includes(v) : true;
          }
        }
        
        // Handle fade transitions
        const isInitialLoad = !field.dataset.condInit;
        const wasVisible = field.classList.contains('is-visible') || field.getAttribute('aria-hidden') === 'false';
        if (show) {
          field.hidden = false;
          field.setAttribute('aria-hidden', 'false');
          if (!isInitialLoad) {
            requestAnimationFrame(() => {
              field.classList.add('is-visible');
            });
          } else {
            field.classList.add('is-visible');
          }
        } else {
          if (!isInitialLoad && wasVisible) {
            this.#resetConditionalPropertyValues(field);
          }
          field.classList.remove('is-visible');
          field.setAttribute('aria-hidden', 'true');
          if (!isInitialLoad) {
            setTimeout(() => {
              field.hidden = true;
            }, 300);
          } else {
            field.hidden = true;
          }
        }
        this.#setFieldsDisabled(field, !show);
      };
      
      if (form) {
        const selector = `[name="properties[${this.#escapeCSS(key)}]"]`;
        form.querySelectorAll(selector).forEach(el => {
          el.addEventListener('change', evaluateCondition);
          el.addEventListener('input', evaluateCondition);
        });
      }
      
      evaluateCondition();
      field.dataset.condInit = 'true';
    });
  }

  /**
   * Read form value for a property key
   * @param {Element | null} form - The form element
   * @param {string} key - The property key
   * @returns {{found: boolean, value: string}}
   */
  #readFormValue(form, key) {
    if (!form) return { found: false, value: '' };
    
    const selector = `[name="properties[${this.#escapeCSS(key)}]"]`;
    const elements = form.querySelectorAll(selector);
    
    if (!elements || elements.length === 0) {
      return { found: false, value: '' };
    }
    
    const first = elements[0];
    if (first instanceof HTMLInputElement && first.type === 'radio') {
      const checked = form.querySelector(`${selector}:checked`);
      return { 
        found: true, 
        value: (checked instanceof HTMLInputElement && checked.value) || '' 
      };
    }
    
    if (first instanceof HTMLInputElement || first instanceof HTMLSelectElement || first instanceof HTMLTextAreaElement) {
      return { found: true, value: first.value || '' };
    }
    
    return { found: false, value: '' };
  }

  /**
   * Clear property-bearing conditional field values before hiding them.
   * @param {HTMLElement} container - The conditional field container
   */
  #resetConditionalPropertyValues(container) {
    if (!container.matches('.custom-select, .custom-multi, .custom-property, .product-file-upload, .swatch-group, .design-selector')) {
      return;
    }

    container.querySelectorAll('input, select, textarea').forEach((el) => {
      if (el instanceof HTMLSelectElement) {
        const before = el.value;
        const hasEmptyOption = Array.from(el.options).some((option) => option.value === '');
        if (hasEmptyOption) {
          el.value = '';
        } else {
          el.selectedIndex = -1;
        }
        this.#resetEnhancedSelectLabel(el);
        if (el.value !== before) this.#emitValueChange(el);
      } else if (el instanceof HTMLInputElement && (el.type === 'checkbox' || el.type === 'radio')) {
        if (el.checked) {
          el.checked = false;
          this.#emitValueChange(el);
        }
      } else if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
        if (el.value !== '') {
          el.value = '';
          this.#emitValueChange(el);
        }
      }
    });
  }

  /**
   * Keep enhanced custom-select text in sync after programmatic reset.
   * @param {HTMLSelectElement} select - The native select backing the enhanced control
   */
  #resetEnhancedSelectLabel(select) {
    const enhanced = select.closest('.js-custom-select');
    if (!enhanced) return;

    const valueEl = enhanced.querySelector('.custom-select__value');
    const placeholder = select.querySelector('option[value=""]')?.textContent || enhanced.querySelector('.custom-select__option--placeholder')?.textContent || '';
    if (valueEl) valueEl.textContent = placeholder;
  }

  /**
   * Dispatch value-change events so dependent conditionals and fee logic update.
   * @param {HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement} el - The changed form control
   */
  #emitValueChange(el) {
    el.dispatchEvent(new Event('input', { bubbles: true }));
    el.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Set disabled state on all form fields within container
   * @param {Element} container - The container element
   * @param {boolean} disabled - Whether to disable the fields
   */
  #setFieldsDisabled(container, disabled) {
    container.querySelectorAll('input, select, textarea, button').forEach(el => {
      if (disabled) {
        if ((el instanceof HTMLInputElement || el instanceof HTMLSelectElement || 
             el instanceof HTMLTextAreaElement) && el.required) {
          el.setAttribute('data-was-required', 'true');
          el.required = false;
        }
        if (el instanceof HTMLInputElement || el instanceof HTMLSelectElement || 
            el instanceof HTMLTextAreaElement || el instanceof HTMLButtonElement) {
          el.disabled = true;
        }
      } else {
        if (el instanceof HTMLInputElement || el instanceof HTMLSelectElement || 
            el instanceof HTMLTextAreaElement || el instanceof HTMLButtonElement) {
          el.disabled = false;
        }
        if ((el instanceof HTMLInputElement || el instanceof HTMLSelectElement || 
             el instanceof HTMLTextAreaElement) && el.getAttribute('data-was-required') === 'true') {
          el.required = true;
          el.removeAttribute('data-was-required');
        }
      }
    });
  }

  /**
   * Escape CSS selector special characters
   * @param {string} str - The string to escape
   * @returns {string}
   */
  #escapeCSS(str) {
    if (window.CSS && CSS.escape) {
      return CSS.escape(str);
    }
    return str;
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
